import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { deleteDoc, limit, onSnapshot, orderBy, query, serverTimestamp, setDoc, writeBatch } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../auth";
import { useI18n } from "../lib/i18n";
import { fetchFromProxy } from "../lib/session";
import {
  CATEGORY_LABEL,
  PRIORITY_HELP,
  PRIORITY_LABEL,
  PRIORITY_ORDER,
  answerDocId,
  countByPriority,
  initialFilter,
  isMaterialQuestion,
  questionAnchorId,
  remainingOf,
  priorityOf,
  sortQuestions,
  stepRemovalKey,
  ingredientEditKey,
  type Priority,
  type ReviewQuestion,
} from "../lib/review";
import {
  editDocId as editDocIdOf,
  hasIngredientEdits,
  hasStepEdits,
  withIngredientEdits,
  withProcedureEdit,
  withStepEdits,
  type EditDoc,
} from "../lib/manual-edits";
import { StepEditPanel } from "../components/StepEditPanel";
import { IngredientEditPanel } from "../components/IngredientEditPanel";
import { RawMaterialsPanel } from "../components/RawMaterialsPanel";
import { rowsOf } from "../lib/raw-materials";
import { PrepDocImport, type PrepJob } from "../components/PrepDocImport";
import { PrepProcedureEditPanel } from "../components/PrepProcedureEditPanel";
import { PrepFailureAlert } from "../components/PrepFailureAlert";
import type { JobDoc } from "../lib/types";

type Filter = Priority | "all" | "unanswered";

interface AnswerDoc {
  questionId?: string;
  answer: string;
  /** 反映したが分量・人数として読めなかった(#115。worker が付ける。保存し直すと外れる) */
  unreadable?: boolean;
  answeredBy: string;
  answeredAt: unknown;
}

/**
 * 工程の削除依頼(#174)。チェックした工程をここに積み、「回答を本文に反映」で
 * resolve が本文から外す。画面は依頼の記録だけを担い、本文は書き換えない(回答と同じ)。
 */
interface RemovalDoc {
  step_no: number;
  name: string;
  start: string;
  end: string;
  requestedBy: string;
  requestedAt: unknown;
}

/**
 * 材料表の追加・削除依頼(#194)。工程の削除と同じく、画面は依頼を記録するだけ。
 * 「回答を本文に反映」で resolve が材料表と工程の参照を書き換える。
 */
interface IngredientEditDoc {
  op: "add" | "remove";
  name: string;
  quantityText?: string | null;
  step_start?: string | null;
  step_end?: string | null;
  requestedBy: string;
  requestedAt: unknown;
}

/**
 * ④ヒアリング内容の回答・登録。
 *
 * 質問は CLI が出した review_questions.json をそのまま使う(認可プロキシ越しに取得)。
 * 画面は並べ替え・絞り込み・回答の記録だけを担い、質問の抽出も回答の反映も行わない。
 * 反映は resolve --sheet(既存)に渡す。二重実装すると必ずズレる。
 */
interface Coverage {
  durationSec: number;
  uncoveredSec: number;
  coveredRatio: number;
  ranges: { start: string; end: string; duration_sec: number; reason: string | null }[];
}

/**
 * マニュアル本文(#133)。
 *
 * レビュー待ちのあいだは、出来上がったマニュアルを読むのではなく
 * 「本文 → その工程の確認事項」を工程順に並べて見せる。確認事項だけを一覧にすると、
 * 何についての問いなのかを本文と往復して確かめることになる。
 */
interface ManualStep {
  step_no: number;
  name: string;
  start: string;
  end: string;
  actions: string;
  heat_level: string | null;
  tools: string[];
  ingredients: { ingredient_id: string; usage_note: string | null }[];
  critical_points: string[];
  common_mistakes: string[];
  completion_criteria: { criteria: string | null } | null;
}

interface ManualIngredient {
  ingredient_id: string;
  name: string;
  role: "ingredient" | "medium";
  quantity: number | null;
  unit: string | null;
  note: string | null;
  prep_note: string | null;
  is_placeholder?: boolean;
  observed_as?: string | null;
  raw_materials?: {
    name: string;
    quantity: number | null;
    unit: string | null;
    yield_pct: number | null;
    note: string | null;
  }[];
}

/** 仕込み(動画外の前工程 #18)。documented はレシピ文書から作り方を取り込んだもの(#250) */
interface ManualPrerequisite {
  name: string;
  reason: string;
  status: "missing" | "linked" | "external" | "documented";
  note: string | null;
  /** 取り込んだ文書。documented で null なら料理長が画面で書いた作り方 */
  prep_doc?: string | null;
  procedure?: {
    ingredients: { name: string; amount: string | null }[];
    steps: string[];
    notes: string[];
  } | null;
}

interface Manual {
  dish_name: string;
  prerequisites?: ManualPrerequisite[];
  servings: number | null;
  servings_note: string | null;
  steps: ManualStep[];
  ingredients_master: ManualIngredient[];
  plating_criteria: string | null;
}


/** 秒を「N分NN秒」に(表示言語は呼び出し側の t で解決する) */
function mmss(sec: number, t: (ja: string, params?: Record<string, string | number>) => string) {
  const m = Math.floor(sec / 60);
  return t("{m}分{s}秒", { m, s: String(Math.round(sec % 60)).padStart(2, "0") });
}

export function Review() {
  const { dishId } = useParams<{ dishId: string }>();
  const { user, paths } = useAuth();
  const { t } = useI18n();
  const [coverage, setCoverage] = useState<Coverage | null>(null);
  const [questions, setQuestions] = useState<ReviewQuestion[] | null>(null);
  const [manual, setManual] = useState<Manual | null>(null);
  const [answers, setAnswers] = useState<Record<string, AnswerDoc>>({});
  const [removals, setRemovals] = useState<Record<string, RemovalDoc>>({});
  const [removalError, setRemovalError] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, IngredientEditDoc>>({});
  // 工程の修正(#219)。「マニュアルを編集」(#203)と同じ記録
  const [manualEdits, setManualEdits] = useState<Record<string, EditDoc>>({});
  const [editingStep, setEditingStep] = useState<string | null>(null);
  // 原料の登録(#222)。開いている材料の名前
  const [editingRaw, setEditingRaw] = useState<string | null>(null);
  // 仕込みの作り方の修正(#259)。開いている仕込みの名前
  const [editingPrep, setEditingPrep] = useState<string | null>(null);
  // 材料の名前・分量の修正(#230)。開いている材料の名前(本文の名前)
  const [editingIng, setEditingIng] = useState<string | null>(null);
  // 材料を他の材料の原料へ移す(#226)。from=移す材料、to=移す先(選ぶまでは null)
  const [movingRaw, setMovingRaw] = useState<{ from: string; to: string | null } | null>(null);
  const [newName, setNewName] = useState("");
  const [newQty, setNewQty] = useState("");
  const [newStep, setNewStep] = useState("");
  const [editBusy, setEditBusy] = useState(false);
  const [togglingKey, setTogglingKey] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState<Filter>("high");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [savingAll, setSavingAll] = useState(false);
  const [savedNote, setSavedNote] = useState<string | null>(null);
  // 仕込みの取り込み(#250)。文書の選択肢と、直近のジョブ
  const [recipeDocs, setRecipeDocs] = useState<string[]>([]);
  const [jobs, setJobs] = useState<(JobDoc & { id: string })[]>([]);

  const load = useCallback(async () => {
    if (!dishId) return;
    setLoadError(null);
    setQuestions(null);
    try {
      // セッションクッキーの発行を待ってから取りに行く。待たないと
      // ログイン直後・リロード直後に 401 になり、画面が空のままになる
      const r = await fetchFromProxy(`/d/${dishId}/review_questions.json`);
      if (!r.ok) {
        throw new Error(
          r.status === 401
            ? t("ログインの有効期限が切れている可能性があります。ログインし直してください (401)")
            : r.status === 404
              ? t("質問がまだ作られていません。品目の画面で「マニュアルを生成」を実行してください (404)")
              : t("質問の取得に失敗しました ({status})", { status: r.status }),
        );
      }
      const d = (await r.json()) as { questions?: ReviewQuestion[] };
      setQuestions(d.questions ?? []);
      // 質問がある優先度で開く(残りが中・低だけの品目で「該当なし」に見せない)
      setFilter(initialFilter(d.questions ?? []));

      // マニュアル本文。工程ごとに「本文 → その工程の確認事項」で並べる(#133)。
      // 取れなくても回答自体はできるので、失敗しても止めない
      try {
        const m = await fetchFromProxy(`/d/${dishId}/analysis.json`);
        if (m.ok) setManual((await m.json()) as Manual);
      } catch {
        /* 本文なしで続行 */
      }
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : String(e));
    }
  }, [dishId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!dishId) return;
    if (!paths) return;
    // 未記録区間（撮影の取りこぼし）。ワーカーが analyze 後に品目へ書く。
    // **確定するとこの解析が採点の基準になる**ので、確定前に知らせる必要がある。
    const stopDish = onSnapshot(paths.dishDoc(dishId), (snap) => {
      setCoverage((snap.get("coverage") as Coverage | null) ?? null);
      setRecipeDocs((snap.get("recipeDocPaths") as string[] | undefined) ?? []);
    });
    // 仕込みの取り込み(#250)の進み具合。単一項目の並べ替えだけにして複合インデックスを要さない
    const stopJobs = onSnapshot(
      query(paths.jobs(dishId), orderBy("createdAt", "desc"), limit(10)),
      (snap) => setJobs(snap.docs.map((d) => ({ id: d.id, ...(d.data() as JobDoc) }))),
      () => setJobs([]),
    );
    const stopAnswers = onSnapshot(
      paths.answers(dishId),
      (snap) => {
        const next: Record<string, AnswerDoc> = {};
        snap.docs.forEach((d) => {
          const data = d.data() as AnswerDoc;
          // 保存側で ID を丸めているため、突き合わせは本文の questionId を正にする
          next[data.questionId ?? d.id] = data;
        });
        setAnswers(next);
      },
      (e) => setSaveError(`${t("回答の読み込みに失敗しました:")} ${e.message}`),
    );
    const stopRemovals = onSnapshot(
      paths.stepRemovals(dishId),
      (snap) => {
        const next: Record<string, RemovalDoc> = {};
        snap.docs.forEach((d) => {
          next[d.id] = d.data() as RemovalDoc;
        });
        setRemovals(next);
      },
      (e) => setRemovalError(`${t("削除依頼の読み込みに失敗しました:")} ${e.message}`),
    );
    const stopEdits = onSnapshot(
      paths.ingredientEdits(dishId),
      (snap) => {
        const next: Record<string, IngredientEditDoc> = {};
        snap.docs.forEach((d) => {
          next[d.id] = d.data() as IngredientEditDoc;
        });
        setEdits(next);
      },
      (e) => setRemovalError(`${t("材料の変更依頼の読み込みに失敗しました:")} ${e.message}`),
    );
    const stopManual = onSnapshot(
      paths.manualEdits(dishId),
      (snap) => {
        const next: Record<string, EditDoc> = {};
        snap.docs.forEach((d) => {
          next[d.id] = d.data() as EditDoc;
        });
        setManualEdits(next);
      },
      (e) => setRemovalError(`${t("編集内容の読み込みに失敗しました:")} ${e.message}`),
    );
    return () => {
      stopDish();
      stopJobs();
      stopAnswers();
      stopRemovals();
      stopEdits();
      stopManual();
    };
  }, [dishId, paths]);

  // 取り込みが終わったら本文と質問を読み直す(仕込みの質問が消え、作り方が本文に入る)
  const seenDone = useRef<Set<string> | null>(null);
  useEffect(() => {
    const done = jobs.filter((j) => j.type === "prep-doc" && j.status === "succeeded").map((j) => j.id);
    if (seenDone.current === null) {
      seenDone.current = new Set(done); // 開いた時点で終わっていたものは読み直さない
      return;
    }
    const fresh = done.filter((id) => !seenDone.current!.has(id));
    fresh.forEach((id) => seenDone.current!.add(id));
    if (fresh.length) void load();
  }, [jobs, load]);
  const activeJob = jobs.some((j) => j.status === "queued" || j.status === "running");
  // 直近の実行が仕込みの取り込みの失敗なら、見出しの直下にアラートとして出す(品目画面と同じ)。
  // 仕込み一覧の同じ仕込みには失敗を重ねて出さない
  const failedPrep =
    jobs[0]?.type === "prep-doc" && jobs[0].status === "failed" && jobs[0].ingredient ? jobs[0].ingredient : null;
  /** その仕込みの直近の取り込みジョブ */
  // この仕込みの直近の取り込み・扱いの切り替え。切り替え(link-prep)の後に古い取り込みの失敗を
  // 出し続けないよう、両方のうち新しいほうを返す(jobs は作成の新しい順)
  function prepJobOf(name: string): PrepJob | null {
    const j = jobs.find((x) => (x.type === "prep-doc" || x.type === "link-prep") && x.ingredient === name);
    return j
      ? { type: j.type, status: j.status, error: j.error, errorKind: j.errorKind ?? null, docPath: j.docPath ?? null }
      : null;
  }

  const sorted = useMemo(() => (questions ? sortQuestions(questions) : []), [questions]);
  const counts = useMemo(() => countByPriority(sorted), [sorted]);
  const answeredCount = sorted.filter((q) => answers[q.id]?.answer).length;
  const recordedCount = sorted.filter((q) => answers[q.id]).length;

  const shown = sorted.filter((q) => {
    if (filter === "all") return true;
    if (filter === "unanswered") return !answers[q.id];
    return priorityOf(q) === filter;
  });
  // 材料表の入力欄(分量・実名・人数)は絞り込みに関係なく常に出す。表が虫食いになると
  // 「どこまで埋めたか」が見えなくなる。カード形式の材料質問と工程の質問は絞り込みに従う
  const questionById = useMemo(() => new Map(sorted.map((q) => [q.id, q] as const)), [sorted]);
  const materialCards = shown.filter(
    (q) => isMaterialQuestion(q) && !["quantity", "identify", "servings"].includes(q.category),
  );
  const stepQuestions = shown.filter((q) => !isMaterialQuestion(q));

  const dirtyOf = useCallback(
    (q: ReviewQuestion) =>
      drafts[q.id] !== undefined && drafts[q.id] !== (answers[q.id]?.answer ?? ""),
    [drafts, answers],
  );
  const dirtyQuestions = sorted.filter(dirtyOf);
  // 現在の本文にある工程のうち、削除にチェックが入っているもの(古い依頼は数えない)
  const pendingRemovals = manual
    ? manual.steps.filter((st) => removals[stepRemovalKey(st)]).length
    : 0;

  function payload(q: ReviewQuestion, value: string) {
    return {
      questionId: q.id,
      category: q.category,
      prompt: q.prompt,
      answer: value,
      answeredBy: user?.uid ?? "",
      answeredAt: serverTimestamp(),
    };
  }

  function clearDrafts(ids: string[]) {
    setDrafts((d) => {
      const next = { ...d };
      ids.forEach((id) => delete next[id]);
      return next;
    });
  }

  /** 1問だけ保存。空欄のまま保存すると「保留」として記録する(捨てない) */
  async function save(q: ReviewQuestion) {
    if (!dishId || !user) return;
    if (!paths) {
      setSaveError(t("テナントが未確定です。ログインし直してください"));
      return;
    }
    const value = (drafts[q.id] ?? answers[q.id]?.answer ?? "").trim();
    setSavingId(q.id);
    setSaveError(null);
    try {
      // ID の丸めは develop 側の修正（回答が保存できない不具合の対処）。そのまま活かす
      await setDoc(paths.answerDoc(dishId, answerDocId(q.id)), payload(q, value));
      clearDrafts([q.id]);
      setSavedNote(value ? t("回答を保存しました") : t("保留として記録しました"));
    } catch (e) {
      setSaveError(`${t("保存に失敗しました:")} ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSavingId(null);
    }
  }

  /** 入力済みのものをまとめて保存。1問ずつ押させると取りこぼす */
  async function saveAll() {
    if (!paths) {
      setSaveError(t("テナントが未確定です。ログインし直してください"));
      return;
    }
    if (!dishId || !user || dirtyQuestions.length === 0) return;
    setSavingAll(true);
    setSaveError(null);
    try {
      // Firestore の1バッチは500件まで。質問は実測47問だが、上限で割る
      for (let i = 0; i < dirtyQuestions.length; i += 400) {
        const chunk = dirtyQuestions.slice(i, i + 400);
        const batch = writeBatch(db);
        for (const q of chunk) {
          batch.set(
            paths.answerDoc(dishId, answerDocId(q.id)),
            payload(q, (drafts[q.id] ?? "").trim()),
          );
        }
        await batch.commit();
      }
      clearDrafts(dirtyQuestions.map((q) => q.id));
      setSavedNote(t("{n}件を保存しました", { n: dirtyQuestions.length }));
    } catch (e) {
      setSaveError(`${t("まとめての保存に失敗しました:")} ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSavingAll(false);
    }
  }

  /**
   * 工程の削除依頼(#174)。チェック=依頼を記録、外す=依頼を消す。
   * 本文から外れるのは「回答を本文に反映」を実行したとき(判断は人、反映は resolve)。
   */
  async function toggleRemoval(st: ManualStep, on: boolean) {
    if (!dishId || !user || !paths || !manual) return;
    const key = stepRemovalKey(st);
    setRemovalError(null);
    if (on) {
      const remaining = manual.steps.filter(
        (s) => stepRemovalKey(s) !== key && !removals[stepRemovalKey(s)],
      );
      if (remaining.length === 0) {
        setRemovalError(t("少なくとも1工程は残す必要があります"));
        return;
      }
    }
    setTogglingKey(key);
    try {
      if (on) {
        await setDoc(paths.stepRemovalDoc(dishId, key), {
          step_no: st.step_no,
          name: st.name,
          start: st.start,
          end: st.end,
          requestedBy: user.uid,
          requestedAt: serverTimestamp(),
        });
      } else {
        await deleteDoc(paths.stepRemovalDoc(dishId, key));
      }
    } catch (e) {
      setRemovalError(`${t("削除の記録に失敗しました:")} ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setTogglingKey(null);
    }
  }

  /** 材料の削除依頼(#194)。チェック=依頼を記録、外す=依頼を消す */
  async function toggleIngredientRemoval(name: string, on: boolean) {
    if (!dishId || !user || !paths) return;
    const key = ingredientEditKey("remove", name);
    setRemovalError(null);
    setEditBusy(true);
    try {
      if (on) {
        await setDoc(paths.ingredientEditDoc(dishId, key), {
          op: "remove", name, requestedBy: user.uid, requestedAt: serverTimestamp(),
        });
      } else {
        await deleteDoc(paths.ingredientEditDoc(dishId, key));
      }
    } catch (e) {
      setRemovalError(`${t("材料の変更依頼の記録に失敗しました:")} ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setEditBusy(false);
    }
  }

  /** 材料の追加依頼(#194)。名前は必須、分量と使う工程は任意 */
  async function addIngredient() {
    if (!dishId || !user || !paths) return;
    const name = newName.trim();
    if (!name) return;
    const st = manual?.steps.find((s) => String(s.step_no) === newStep);
    setRemovalError(null);
    setEditBusy(true);
    try {
      await setDoc(paths.ingredientEditDoc(dishId, ingredientEditKey("add", name)), {
        op: "add",
        name,
        quantityText: newQty.trim() || null,
        step_start: st?.start ?? null,
        step_end: st?.end ?? null,
        requestedBy: user.uid,
        requestedAt: serverTimestamp(),
      });
      setNewName("");
      setNewQty("");
      setNewStep("");
    } catch (e) {
      setRemovalError(`${t("材料の変更依頼の記録に失敗しました:")} ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setEditBusy(false);
    }
  }

  async function cancelEdit(key: string) {
    if (!dishId || !paths) return;
    setEditBusy(true);
    try {
      await deleteDoc(paths.ingredientEditDoc(dishId, key));
    } catch (e) {
      setRemovalError(`${t("材料の変更依頼の記録に失敗しました:")} ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setEditBusy(false);
    }
  }

  if (loadError) {
    return (
      <div className="page">
        <p><Link to={`/dishes/${dishId}`}>{t("← 品目")}</Link></p>
        <p className="error">{loadError}</p>
        <button onClick={() => void load()}>{t("もう一度読み込む")}</button>
      </div>
    );
  }
  if (!questions) return <div className="page"><p className="muted">{t("読み込み中…")}</p></div>;

  if (sorted.length === 0 && !manual) {
    return (
      <div className="page">
        <p><Link to={`/dishes/${dishId}`}>{t("← 品目")}</Link></p>
        <h1>{t("レビュー")}</h1>
        <p className="muted">
          {t("この品目に確認事項はありません。回答なしで「回答を本文に反映」へ進めます。")}
        </p>
      </div>
    );
  }

  return (
    <div className="page">
      <p><Link to={`/dishes/${dishId}`}>{t("← 品目")}</Link></p>
      <header className="page-head">
        <h1>{manual ? t("マニュアルの確認: {name}", { name: manual.dish_name }) : t("レビュー")}</h1>
        {sorted.length > 0 && (
          <span className="muted small">
            {t("{a} / {b} 問 回答済み", { a: answeredCount, b: sorted.length })}
            {recordedCount > answeredCount && t("（保留 {n}）", { n: recordedCount - answeredCount })}
          </span>
        )}
      </header>
      {renderRemaining()}
      {failedPrep && dishId && (
        <PrepFailureAlert
          dishId={dishId}
          ingredient={failedPrep}
          jobs={jobs}
          recipeDocs={recipeDocs}
          edits={manualEdits}
          busy={activeJob}
        />
      )}
      <p className="muted small">
        {sorted.length === 0
          ? t("この品目に確認事項はありません。不要な工程があれば下でチェックし、「回答を本文に反映」で外せます。")
          : manual
            ? t("工程ごとに、マニュアルの本文とその工程の確認事項を並べています。本文を見ながらその場で答えてください。")
            : t("確認事項に答えてください。")}
        {sorted.length > 0 &&
          t("入力した内容はこの画面で保存され、品目の画面の「回答を本文に反映」でマニュアルへ落ちます。分からない項目は空欄のまま保存すると保留として残せます。")}
        {manual &&
          t("不要な工程(他の料理の映り込みなど)は、各工程の「この工程をマニュアルから削除する」にチェックすると、反映時に本文から外れます。")}
        {manual &&
          t("本文の内容を直したいとき（例: 1cmに切る → 5cmに切る）は、各工程の「この工程を修正する」から直せます。")}
      </p>

      {pendingRemovals > 0 && (
        <div className="warn">
          <strong>{t("削除する工程 {n} 件", { n: pendingRemovals })}</strong>{" "}
          {t("品目の画面の「回答を本文に反映」を実行すると本文から外れ、工程番号は振り直されます。")}
        </div>
      )}
      {removalError && <p className="error">{removalError}</p>}

      {coverage && coverage.ranges.length > 0 && (coverage.uncoveredSec >= 60 || coverage.coveredRatio < 0.95) && (
        <div className="warn coverage-warn">
          <strong>⚠ {t("撮影されていない区間があります")}</strong>
          <p>
            {t("この動画は全体 {total} のうち {ratio}% しか撮影されていません（未記録 {missing}）。カメラが止まっている間に進んだ工程は解析に含まれないため、このまま確定すると、その工程は採点でも抜けます。", {
              total: mmss(coverage.durationSec, t),
              ratio: Math.round(coverage.coveredRatio * 100),
              missing: mmss(coverage.uncoveredSec, t),
            })}
          </p>
          <ul>
            {coverage.ranges.map((r, i) => (
              <li key={i}>
                {r.start}〜{r.end}（{mmss(r.duration_sec, t)}）{r.reason ? `：${r.reason}` : ""}
              </li>
            ))}
          </ul>
          <p>{t("工程が欠けている場合は、確定せずに撮り直しをご検討ください。")}</p>
        </div>
      )}

      {sorted.length > 0 && (
      <div className="filters">
        {PRIORITY_ORDER.map((p) => (
          <button
            key={p}
            className={`chip ${filter === p ? "on" : ""}`}
            onClick={() => setFilter(p)}
            title={t(PRIORITY_HELP[p])}
          >
            {t("優先度")}{t(PRIORITY_LABEL[p])} <span className="muted">{counts[p]}</span>
          </button>
        ))}
        <button className={`chip ${filter === "unanswered" ? "on" : ""}`} onClick={() => setFilter("unanswered")}>
          {t("未回答")} <span className="muted">{sorted.length - recordedCount}</span>
        </button>
        <button className={`chip ${filter === "all" ? "on" : ""}`} onClick={() => setFilter("all")}>
          {t("すべて")} <span className="muted">{sorted.length}</span>
        </button>
      </div>
      )}
      {sorted.length > 0 && filter !== "all" && filter !== "unanswered" && (
        <p className="muted small">{t(PRIORITY_HELP[filter])}</p>
      )}

      {sorted.length > 0 && shown.length === 0 && (
        <p className="muted">{t("該当する質問はありません。")}</p>
      )}

      {/* 工程順に「本文 → その工程の確認事項」で並べる(#133)。
          本文が取れないときは従来どおり確認事項だけを出す */}
      {manual
        ? [
            // 特定の工程に属さない確認事項(工程の構成・警告など)は先頭に出す。
            // 末尾に置くと、工程の本文を最後までたどらないと見つからない(ぶっとびスープで見落とされた)
            renderGlobalQuestions(),
            renderMaterials(),
            ...manual.steps.map((original) => {
              const mine = stepQuestions.filter((q) => q.step_no === original.step_no);
              const key = stepRemovalKey(original);
              const marked = !!removals[key];
              // 修正(#219)は反映まで本文に入らないので、表示だけ重ねて「反映待ち」と示す
              const st = withStepEdits(original, key, manualEdits);
              const isLast = original.step_no === manual.steps[manual.steps.length - 1]?.step_no;
              const platingEdit = manualEdits[editDocIdOf("dish", "", "plating_criteria")];
              const plating = platingEdit ? platingEdit.value.trim() || null : manual.plating_criteria;
              const pendingFix = hasStepEdits(key, manualEdits) || (isLast && !!platingEdit);
              return (
                <section key={st.step_no} className={`step-block${marked ? " removed" : ""}`}>
                  <h2>
                    {t("工程{n}:", { n: st.step_no })} {st.name}
                    <span className="muted small">　{st.start}〜{st.end}</span>
                    {mine.length > 0 && (
                      <span className="badge p-high">{t("確認事項 {n}", { n: mine.length })}</span>
                    )}
                    {pendingFix && <span className="badge p-medium">{t("修正あり（反映待ち）")}</span>}
                  </h2>
                  {/* 不要な工程の削除(#174)。判断は人。反映は「回答を本文に反映」 */}
                  <label className="remove-step">
                    <input
                      type="checkbox"
                      checked={marked}
                      disabled={togglingKey === key}
                      onChange={(e) => void toggleRemoval(st, e.target.checked)}
                    />
                    {t("この工程をマニュアルから削除する")}
                    {marked && <span className="muted small">（{t("反映時に削除されます")}）</span>}
                  </label>
                  {!marked && editingStep !== key && (
                    <button className="link fix-step" onClick={() => setEditingStep(key)}>
                      {t("この工程を修正する")}
                    </button>
                  )}
                  {editingStep === key && dishId && (
                    <StepEditPanel
                      dishId={dishId}
                      step={original}
                      stepKey={key}
                      edits={manualEdits}
                      plating={isLast ? manual.plating_criteria : undefined}
                      onClose={() => setEditingStep(null)}
                    />
                  )}
                  <div className="card step-body">
                    <img
                      className="step-shot"
                      src={`/d/${dishId}/frames/step_${String(st.step_no).padStart(2, "0")}.jpg`}
                      alt=""
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = "none";
                      }}
                    />
                    <p className="step-actions">{st.actions}</p>
                    <dl className="step-meta">
                      {st.heat_level && (
                        <>
                          <dt>{t("火加減")}</dt>
                          <dd>{st.heat_level}</dd>
                        </>
                      )}
                      {st.ingredients.length > 0 && (
                        <>
                          <dt>{t("投入する食材")}</dt>
                          <dd>{st.ingredients.map((r) => nameOf(r.ingredient_id)).join(" / ")}</dd>
                        </>
                      )}
                      {st.tools.length > 0 && (
                        <>
                          <dt>{t("使用器具")}</dt>
                          <dd>{st.tools.join(" / ")}</dd>
                        </>
                      )}
                      {st.completion_criteria?.criteria && (
                        <>
                          <dt>{t("完了基準")}</dt>
                          <dd>{st.completion_criteria.criteria}</dd>
                        </>
                      )}
                      {st.critical_points.length > 0 && (
                        <>
                          <dt>{t("重要ポイント")}</dt>
                          <dd>
                            <ul className="plain-list">
                              {st.critical_points.map((c, i) => (
                                <li key={i}>{c}</li>
                              ))}
                            </ul>
                          </dd>
                        </>
                      )}
                      {/* 盛り付け基準は工程ではないが、盛り付けについての記述。
                          最後の工程の本文として出し、皿の形などの問いと並べる(#137) */}
                      {isLast && plating && (
                          <>
                            <dt>{t("盛り付け基準")}</dt>
                            <dd>{plating}</dd>
                          </>
                        )}
                    </dl>
                  </div>
                  {mine.map(renderQuestion)}
                </section>
              );
            }),
          ]
        : shown.map(renderQuestion)}

      {saveError && <p className="error">{saveError}</p>}

      {/* 1問ずつ押させると取りこぼす。未保存があるあいだ画面下に出しておく */}
      {(dirtyQuestions.length > 0 || savedNote) && (
        <div className="save-bar">
          {dirtyQuestions.length > 0 ? (
            <>
              <span>{t("未保存 {n}件", { n: dirtyQuestions.length })}</span>
              <button onClick={() => void saveAll()} disabled={savingAll}>
                {savingAll ? t("保存中…") : t("入力した内容をまとめて保存")}
              </button>
            </>
          ) : (
            <span className="ok">{savedNote}</span>
          )}
        </div>
      )}
    </div>
  );

  /** 食材IDを名前に。材料表に無ければIDのまま出す(取りこぼさない) */
  function nameOf(id: string): string {
    return manual?.ingredients_master.find((m) => m.ingredient_id === id)?.name ?? id;
  }

  /** 分量・実名・人数の入力欄。質問が無い(記入済み)ものは値を表示するだけ */
  function fieldFor(qid: string, placeholder: string, filled: string | null) {
    const q = questionById.get(qid);
    if (!q) return <span>{filled ?? "—"}</span>;
    const saved = answers[q.id];
    const draft = drafts[q.id] ?? saved?.answer ?? "";
    return (
      <span className="mat-field" id={questionAnchorId(q.id)}>
        <input
          type="text"
          value={draft}
          placeholder={placeholder}
          onChange={(e) => setDrafts((d) => ({ ...d, [q.id]: e.target.value }))}
        />
        {saved?.answer && !dirtyOf(q) && !saved.unreadable && <span className="ok small">{t("保存済み")}</span>}
        {saved?.unreadable && !dirtyOf(q) && <UnreadableNote />}
      </span>
    );
  }

  /**
   * 残りの確認事項(未回答と保留)を見出しの直下にまとめる。何を答えればよいかと置き場所を出し、
   * 「ここへ移動」でその入力欄まで送る。質問は材料表・各工程・全体に散っているため、
   * 残りが1〜2問になると見つからない(2026-09-30 柴田の指示「残りのレビューを分かるように」)
   */
  function renderRemaining() {
    const stepNos = new Set((manual?.steps ?? []).map((s) => s.step_no));
    const items = remainingOf(sorted, answers, stepNos);
    if (items.length === 0) return null;
    const jump = (id: string) => {
      // 絞り込みで隠れていることがあるので「すべて」にしてから送る
      setFilter("all");
      window.setTimeout(() => {
        const el = document.getElementById(questionAnchorId(id));
        if (!el) return;
        // 即時に送る。なめらかスクロールは絞り込みの切り替え直後の描き直しで打ち消され、
        // 入力欄にだけフォーカスが当たって画面が動かなかった(本番で確認)
        el.scrollIntoView({ block: "center" });
        el.querySelector<HTMLInputElement | HTMLTextAreaElement>("input, textarea")?.focus({ preventScroll: true });
      }, 50);
    };
    return (
      <div className="card remaining">
        <strong>{t("残りの確認事項 {n} 問", { n: items.length })}</strong>
        <p className="muted small">{t("すべて答えると「回答を本文に反映」から承認へ進めます。")}</p>
        <ol>
          {items.map((it) => (
            <li key={it.id}>
              <span className="badge p-medium">
                {it.where === "materials"
                  ? t("材料表")
                  : it.where === "step"
                    ? t("工程{n}", { n: it.stepNo ?? "" })
                    : t("全体の確認事項")}
              </span>{" "}
              {it.text}
              {it.held && <span className="muted small">{t("（保留中）")}</span>}
              {it.unreadable && <span className="error small">{t("（数値として読めませんでした）")}</span>}{" "}
              <button className="link" onClick={() => jump(it.id)}>
                {t("ここへ移動")}
              </button>
            </li>
          ))}
        </ol>
      </div>
    );
  }

  /** 特定の工程に属さない確認事項(工程の構成・自動検出の警告など)。本文の先頭に出す */
  function renderGlobalQuestions() {
    if (!manual) return null;
    const rest = stepQuestions.filter(
      (q) => q.step_no == null || !manual.steps.some((st) => st.step_no === q.step_no),
    );
    if (rest.length === 0) return null;
    return (
      <section key="global" className="step-block">
        <h2>
          {t("全体の確認事項")}
          <span className="badge p-high">{t("確認事項 {n}", { n: rest.length })}</span>
        </h2>
        <p className="muted small">
          {t("工程の構成・自動検出の警告など、特定の工程に属さない項目です。")}
        </p>
        {rest.map(renderQuestion)}
      </section>
    );
  }

  /** 材料・調味料・分量を1箇所にまとめた表(工程には散らさない) */
  function renderMaterials() {
    if (!manual) return null;
    // 媒体(油・水など)は表に出さないが、仮名(調理油A 等)は名前の入力欄が要るので出す。
    // 外すと実名の質問がどこにも表示されず、答えられないまま承認が止まる(切干大根の玉子焼き)
    const foods = manual.ingredients_master.filter((m) => m.role !== "medium" || m.is_placeholder);
    const usedIn = (id: string) =>
      manual.steps
        .filter((st) => st.ingredients.some((r) => r.ingredient_id === id))
        .map((st) => t("工程{n}", { n: st.step_no }))
        .join("・");
    const pending = foods.filter(
      (m) => questionById.has(`qty:${m.ingredient_id}`) || questionById.has(`identify:${m.ingredient_id}`),
    ).length + (questionById.has("servings") ? 1 : 0);
    const pendingAdds = Object.entries(edits).filter(([, e]) => e.op === "add");
    const pendingRemoves = Object.values(edits).filter((e) => e.op === "remove").length;
    return (
      <section key="materials" className="step-block materials">
        <h2>
          {t("材料・調味料・分量")}
          {pending > 0 && <span className="badge p-low">{t("未記入 {n}", { n: pending })}</span>}
          {materialCards.length > 0 && (
            <span className="badge p-high">{t("確認事項 {n}", { n: materialCards.length })}</span>
          )}
          {pendingAdds.length + pendingRemoves > 0 && (
            <span className="badge p-medium">{t("変更待ち {n}", { n: pendingAdds.length + pendingRemoves })}</span>
          )}
        </h2>
        <p className="muted small">
          {t("分量は1皿あたりを数値と単位で書いてください（例: 225g、2尾、大さじ1）。名前が「調味料A」のような仮名のものは、実際の名前を書いてください。この表は優先度の絞り込みに関係なく常に表示します。")}
        </p>
        <div className="card">
          <table className="materials-table">
            <thead>
              <tr>
                <th>{t("食材・調味料")}</th>
                <th>{t("分量（1皿あたり）")}</th>
                <th>{t("使用工程")}</th>
              </tr>
            </thead>
            <tbody>
              <tr className="servings-row">
                <td data-label={t("食材・調味料")}>{t("人数（何皿分）")}</td>
                <td data-label={t("分量（1皿あたり）")}>
                  {fieldFor(
                    "servings",
                    t("例: 2人前"),
                    manual.servings != null
                      ? `${manual.servings}${manual.servings_note ? `（${manual.servings_note}）` : ""}`
                      : null,
                  )}
                </td>
                <td className="muted usage-cell" data-label={t("使用工程")}>—</td>
              </tr>
              {foods.map((m) => {
                const placeholder = !!m.is_placeholder;
                // 名前・分量の修正(#230)は反映まで本文に入らないので、表示だけ重ねて「反映待ち」と示す
                const v = withIngredientEdits(m, manualEdits);
                const fixed = hasIngredientEdits(m.name, manualEdits);
                const filled =
                  v.quantity != null ? `${v.quantity}${v.unit ?? ""}${v.note ? `（${v.note}）` : ""}` : v.note;
                return (
                  <Fragment key={m.ingredient_id}>
                  <tr className={placeholder ? "placeholder" : undefined}>
                    <td data-label={t("食材・調味料")}>
                      <div className="mat-name">
                        {v.name}
                        {placeholder && v.name === m.name && <span className="badge p-high">{t("名前未登録")}</span>}
                        {fixed && <span className="badge p-medium">{t("修正あり（反映待ち）")}</span>}
                      </div>
                      {v.name !== m.name && (
                        <div className="muted small">{t("元の名前: {name}", { name: m.name })}</div>
                      )}
                      {placeholder && m.observed_as && (
                        <div className="muted small">{t("映像での見え方: {s}", { s: m.observed_as })}</div>
                      )}
                      {placeholder && fieldFor(`identify:${m.ingredient_id}`, t("実際の名前"), null)}
                      {/* 説明(prep_note)の修正も反映待ちを重ねて出す。空にした修正なら消えて見える */}
                      {v.prep_note && <div className="muted small">{v.prep_note}</div>}
                      {/* 原料と歩留まり(#216)。レビュー画面からも登録できる(#222)。
                          反映前は記録済みの値を出し、「反映待ち」と示す */}
                      {(() => {
                        const rawEdit = manualEdits[editDocIdOf("ingredient", m.name, "raw_materials")];
                        const raws = rawEdit
                          ? rowsOf(rawEdit.value).map((r) => ({
                              name: r.name,
                              quantity: r.quantity ? Number(r.quantity) : null,
                              unit: r.unit || null,
                              yield_pct: r.yield_pct ? Number(r.yield_pct) : null,
                            }))
                          : (m.raw_materials ?? []);
                        return (
                          <>
                            {raws.length > 0 && (
                              <ul className="muted small mat-raw">
                                {raws.map((r) => (
                                  <li key={r.name}>
                                    {t("原料: {name}", { name: r.name })}
                                    {r.quantity != null && ` ${r.quantity}${r.unit ?? ""}`}
                                    {r.yield_pct != null && `（${t("歩留まり {n}%", { n: r.yield_pct })}）`}
                                  </li>
                                ))}
                              </ul>
                            )}
                            {rawEdit && <span className="badge p-medium">{t("原料の変更あり（反映待ち）")}</span>}
                            {editingIng !== m.name && editingRaw !== m.name && movingRaw?.from !== m.name && (
                              <button
                                className="link fix-raw"
                                onClick={() => {
                                  setEditingRaw(null);
                                  setMovingRaw(null);
                                  setEditingIng(m.name);
                                }}
                              >
                                {t("この材料を修正する")}
                              </button>
                            )}
                            {editingRaw !== m.name && movingRaw?.from !== m.name && (
                              <button
                                className="link fix-raw"
                                onClick={() => {
                                  setEditingIng(null);
                                  setMovingRaw(null);
                                  setEditingRaw(m.name);
                                }}
                              >
                                {raws.length ? t("原料を直す") : t("原料を登録")}
                              </button>
                            )}
                            {/* 他の材料の原料へ移す(#226)。削除済みにした材料・移す先が無いときは出さない */}
                            {foods.length > 1 && !edits[ingredientEditKey("remove", m.name)] &&
                              (movingRaw?.from === m.name ? (
                                <label className="move-raw">
                                  {t("どの材料の原料にしますか？")}
                                  <select
                                    value={movingRaw.to ?? ""}
                                    onChange={(e) => setMovingRaw({ from: m.name, to: e.target.value || null })}
                                  >
                                    <option value="">{t("選んでください")}</option>
                                    {foods
                                      .filter((o) => o.ingredient_id !== m.ingredient_id)
                                      .map((o) => (
                                        <option key={o.ingredient_id} value={o.name}>
                                          {withIngredientEdits(o, manualEdits).name}
                                        </option>
                                      ))}
                                  </select>
                                  <button className="link" onClick={() => setMovingRaw(null)}>
                                    {t("やめる")}
                                  </button>
                                </label>
                              ) : (
                                <button
                                  className="link fix-raw"
                                  onClick={() => {
                                    setEditingIng(null);
                                    setEditingRaw(null);
                                    setMovingRaw({ from: m.name, to: null });
                                  }}
                                >
                                  {t("この材料を他の材料の原料にする")}
                                </button>
                              ))}
                          </>
                        );
                      })()}
                      {/* 材料の削除(#194)。判断は人。反映は「回答を本文に反映」 */}
                      <label className="remove-step remove-ingredient">
                        <input
                          type="checkbox"
                          checked={!!edits[ingredientEditKey("remove", m.name)]}
                          disabled={editBusy}
                          onChange={(e) => void toggleIngredientRemoval(m.name, e.target.checked)}
                        />
                        {t("この材料を削除する")}
                      </label>
                    </td>
                    <td data-label={t("分量（1皿あたり）")}>{fieldFor(`qty:${m.ingredient_id}`, t("例: 225g"), filled)}</td>
                    <td className="muted small usage-cell" data-label={t("使用工程")}>{usedIn(m.ingredient_id) || "—"}</td>
                  </tr>
                  {/* 名前・分量の修正(#230)。原料と同じく行の直下に全幅で出す */}
                  {editingIng === m.name && dishId && (
                    <tr className="raw-edit-row">
                      <td colSpan={3}>
                        <IngredientEditPanel
                          dishId={dishId}
                          ingredient={m}
                          otherNames={foods
                            .filter((o) => o.ingredient_id !== m.ingredient_id)
                            .map((o) => withIngredientEdits(o, manualEdits).name)}
                          edits={manualEdits}
                          onClose={() => setEditingIng(null)}
                        />
                      </td>
                    </tr>
                  )}
                  {/* 原料の入力(#222)。セルでは狭いので行の直下に全幅で出す */}
                  {editingRaw === m.name && dishId && (
                    <tr className="raw-edit-row">
                      <td colSpan={3}>
                        <RawMaterialsPanel
                          dishId={dishId}
                          ingredient={m}
                          edits={manualEdits}
                          onClose={() => setEditingRaw(null)}
                        />
                      </td>
                    </tr>
                  )}
                  {/* 原料へ移す(#226)。移す先の原料欄を、移す材料の行の直下に開く */}
                  {movingRaw?.from === m.name && movingRaw.to && dishId && (() => {
                    const target = foods.find((o) => o.name === movingRaw.to);
                    if (!target) return null;
                    return (
                      <tr className="raw-edit-row">
                        <td colSpan={3}>
                          <RawMaterialsPanel
                            key={target.ingredient_id}
                            dishId={dishId}
                            ingredient={target}
                            edits={manualEdits}
                            moveFrom={{ name: m.name, quantity: m.quantity, unit: m.unit, note: m.note }}
                            onClose={() => setMovingRaw(null)}
                          />
                        </td>
                      </tr>
                    );
                  })()}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          {/* 追加待ちの材料(#194)。反映するまで表には載らないので、ここに出す */}
          {pendingAdds.length > 0 && (
            <ul className="pending-adds">
              {pendingAdds.map(([key, e]) => (
                <li key={key}>
                  <span className="badge p-medium">{t("追加待ち")}</span> {e.name}
                  {e.quantityText && <span className="muted"> {e.quantityText}</span>}
                  {e.step_start && (
                    <span className="muted small"> {t("（{s}〜{e} の工程で使用）", { s: e.step_start, e: e.step_end ?? "" })}</span>
                  )}
                  <button className="link" disabled={editBusy} onClick={() => void cancelEdit(key)}>{t("取り消す")}</button>
                </li>
              ))}
            </ul>
          )}
          <div className="add-ingredient">
            <strong className="small">{t("材料を追加する")}</strong>
            <div className="add-ingredient-row">
              <input
                type="text"
                value={newName}
                placeholder={t("食材・調味料の名前（例: 醤油）")}
                onChange={(e) => setNewName(e.target.value)}
              />
              <input
                type="text"
                value={newQty}
                placeholder={t("分量（例: 10ml・任意）")}
                onChange={(e) => setNewQty(e.target.value)}
              />
              <select value={newStep} onChange={(e) => setNewStep(e.target.value)}>
                <option value="">{t("使う工程（任意）")}</option>
                {manual.steps.map((st) => (
                  <option key={st.step_no} value={String(st.step_no)}>
                    {t("工程{n}", { n: st.step_no })} {st.name}
                  </option>
                ))}
              </select>
              <button disabled={editBusy || !newName.trim()} onClick={() => void addIngredient()}>
                {t("追加する")}
              </button>
            </div>
            <p className="muted small">
              {t("追加・削除は「回答を本文に反映」で材料表と工程に反映されます。削除した材料は作り直しても戻りません。")}
            </p>
          </div>
        </div>
        {renderPrerequisites()}
        {materialCards.map(renderQuestion)}
      </section>
    );
  }

  /**
   * 仕込みの一覧(#18 #250)。質問に答えて反映した後も、ここから作り方をレシピで取り込める。
   * 質問が残っている仕込みは質問のカードに取り込みを出すので、ここでは状態だけを出す
   */
  function renderPrerequisites() {
    const prereqs = manual?.prerequisites ?? [];
    if (!prereqs.length || !dishId) return null;
    const STATUS: Record<ManualPrerequisite["status"], string> = {
      missing: "作り方が未記録",
      linked: "仕込みSOPあり",
      external: "市販品・外部調達",
      documented: "レシピから取り込み済み",
    };
    return (
      <div className="card prereq-list">
        <strong className="small">{t("仕込み（この料理の前に必要な準備）")}</strong>
        <ul className="plain-list">
          {prereqs.map((p) => {
            const asked = questionById.has(`prereq:${p.name}`);
            return (
              <li key={p.name}>
                <span className="mat-name">{p.name}</span>{" "}
                <span className={`badge ${p.status === "missing" ? "p-high" : "p-low"}`}>
                  {p.status === "documented" && !p.prep_doc ? t("作り方を記入済み") : t(STATUS[p.status])}
                </span>
                {p.note && <span className="muted small">（{p.note}）</span>}
                {p.status === "documented" && p.procedure && (() => {
                  // 作り方の修正(#259)は反映まで本文に入らないので、表示だけ重ねて「反映待ち」と示す
                  const { procedure: proc, pending } = withProcedureEdit(p.name, p.procedure, manualEdits);
                  if (!proc) return null;
                  return (
                    <>
                      {pending && <span className="badge p-medium">{t("修正あり（反映待ち）")}</span>}
                      <div className="prep-proc small">
                        {proc.ingredients.length > 0 && (
                          <p>
                            <strong>{t("材料")}</strong>{" "}
                            {proc.ingredients.map((i) => (i.amount ? `${i.name} ${i.amount}` : i.name)).join("、")}
                          </p>
                        )}
                        <ol>
                          {proc.steps.map((st, i) => (
                            <li key={i}>{st}</li>
                          ))}
                        </ol>
                        {proc.notes.map((n, i) => (
                          <p key={i} className="muted">{n}</p>
                        ))}
                      </div>
                      {editingPrep === p.name ? (
                        <PrepProcedureEditPanel
                          dishId={dishId}
                          name={p.name}
                          current={p.procedure}
                          edits={manualEdits}
                          onClose={() => setEditingPrep(null)}
                        />
                      ) : (
                        <button className="link fix-raw" onClick={() => setEditingPrep(p.name)}>
                          {t("作り方を修正する")}
                        </button>
                      )}
                    </>
                  );
                })()}
                {(p.status === "documented" || (p.status === "missing" && !asked)) && (
                  <PrepDocImport
                    dishId={dishId}
                    name={p.name}
                    recipeDocs={recipeDocs}
                    job={prepJobOf(p.name)}
                    busy={activeJob}
                    documented={p.status === "documented"}
                    edits={manualEdits}
                    hideFailure={p.name === failedPrep}
                  />
                )}
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  function renderQuestion(q: ReviewQuestion) {
        const saved = answers[q.id];
        const draft = drafts[q.id] ?? saved?.answer ?? "";
        const dirty = dirtyOf(q);
        return (
          <div key={q.id} id={questionAnchorId(q.id)} className="card question">
            <div className="q-head">
              <span className={`badge p-${priorityOf(q)}`}>{t("優先度")}{t(PRIORITY_LABEL[priorityOf(q)])}</span>
              <span className="muted small">{t(CATEGORY_LABEL[q.category])}</span>
              {saved?.answer && <span className="ok small">{t("回答済み")}</span>}
              {saved && !saved.answer && <span className="muted small">{t("保留")}</span>}
            </div>
            <p className="q-prompt">{q.prompt}</p>
            {saved?.unreadable && !dirty && <UnreadableNote />}
            {q.sub?.map((s, i) => (
              <p key={i} className="muted small q-sub">{s}</p>
            ))}
            {q.options && q.options.length > 0 && (
              <div className="q-options">
                {q.options.map((o, i) => (
                  <button
                    key={i}
                    className="chip"
                    onClick={() => setDrafts((d) => ({ ...d, [q.id]: o }))}
                  >
                    {o.length > 70 ? `${o.slice(0, 70)}…` : o}
                  </button>
                ))}
              </div>
            )}
            <textarea
              rows={2}
              value={draft}
              placeholder={t("回答（空欄のまま保存すると保留になります）")}
              onChange={(e) => setDrafts((d) => ({ ...d, [q.id]: e.target.value }))}
            />
            <button onClick={() => save(q)} disabled={savingId === q.id || savingAll || (!dirty && !!saved)}>
              {savingId === q.id ? t("保存中…") : dirty ? t("回答を保存") : saved ? t("保存済み") : t("保留として登録")}
            </button>
            {/* 仕込みの動画が無くても、作り方がレシピにあれば取り込める(#250)。取り込むとこの質問は消える */}
            {q.category === "prerequisite" && dishId && typeof q.ref.name === "string" && (
              <PrepDocImport
                dishId={dishId}
                name={q.ref.name}
                recipeDocs={recipeDocs}
                job={prepJobOf(q.ref.name)}
                busy={activeJob}
                edits={manualEdits}
                hideFailure={q.ref.name === failedPrep}
              />
            )}
          </div>
        );
  }
}

/**
 * 分量・人数の回答が数値として読めなかったときの案内(#115)。
 * 反映しても値は入らず質問が残るので、何をどう書き直せばよいかをその場に出す。
 */
function UnreadableNote() {
  const { t } = useI18n();
  return (
    <span className="error small">
      {t("数値として読めませんでした。「225g」「2人前」のように数と単位で書き直してください。")}
    </span>
  );
}
